import React from 'react';
import styled, { css } from '../../styled';
import { Chevron } from '../icons/outline/Chevron';
import { Tick } from '../icons/solid/Tick';
import DropdownOptionWrapper from './DropdownOptionWrapper';
import DropdownOption from './DropdownOption';
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const generateDefaultBorderStyle = (isOpen) => css `
    ${({ theme }) => `border: 1px solid ${isOpen ? theme.colors.PRIMARY_600 : theme.colors.INK_300}`};
    &:hover {
      ${({ theme }) => `border: 1px solid ${theme.colors.PRIMARY_600}`}
    }
  `;
export const Selector = styled.div `
  color: ${({ theme }) => theme.colors.INK_800};
  ${({ disabledShadow, theme }) => !disabledShadow &&
    `box-shadow: 0 0.0625rem 0.125rem 0.0625rem ${theme.colors.SHADOW_DEFAULT};`}
  background-color: ${({ theme }) => theme.colors.SNOW_100};
  ${({ contentPadding }) => `padding: ${contentPadding || '0.4375rem 0.25rem'};`}
  border-radius: 6px;
  font-size: 1rem;
  display: flex;
  justify-content: flex-start;
  width: ${({ width }) => width};
  ${({ minHeight }) => minHeight ? `min-height:${minHeight}` : 'height: 2.125rem'};
  ${({ minHeight }) => minHeight && 'align-items: center;'};
  height: 2.125rem;
  box-sizing: border-box;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.PRIMARY_600};
    ${({ disabledShadow, theme }) => !disabledShadow &&
    `box-shadow: 0 0.0625rem 0.125rem 0.0625rem
      ${theme.colors.SHADOW_DEFAULT}, 0 0 1.5rem 0 ${theme.colors.SHADOW_LIGHT};`}
    > svg {
      fill: ${({ theme }) => theme.colors.PRIMARY_600};
    }
  }

  ${({ useDefaultBorder, isOpen }) => useDefaultBorder && generateDefaultBorderStyle(isOpen)}
  
  ${({ border }) => (border ? `border: ${border}` : '')};

  &:active {
    color: ${({ theme }) => theme.colors.PRIMARY_700};
    > svg {
      fill: ${({ theme }) => theme.colors.PRIMARY_700};
    }
  }

  > span {
    padding-left: 0.75rem;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: calc(100% - 1.25rem);
  }
  > svg {
    transition: transform 0.3s ease-in-out;
  }
`;
const StyledInput = styled.input `
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  box-sizing: border-box;
  padding: 0;
  padding-left: 0.75rem;
  font-size: 1rem;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.INK_800};

  &::placeholder {
    color: ${({ theme }) => theme.colors.INK_400};
    font-size: 1rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Noto Sans', Roboto, Arial;
  }

  &:disabled {
    background-color: transparent;
  }
`;
const Container = styled.span `
  display: ${({ block }) => (block ? 'block' : 'inline-block')};
  position: relative;
`;
const Checkbox = styled.div `
  width: 1rem;
  height: 1rem;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.INK_300};
  background-color: ${({ theme }) => theme.colors.SNOW_100};
  display: inline-flex;
  position: relative;

  > svg {
    visibility: hidden;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;
const CheckboxWrapper = styled.div `
  display: flex;
  color: ${({ theme }) => theme.colors.INK_800};
  padding: 0.75rem;
  cursor: pointer;

  > span {
    display: inline-flex;
    padding-left: 0.5rem;
  }

  ${props => props.selected === true &&
    css `
      ${Checkbox} {
        border: 1px solid ${({ theme }) => theme.colors.PRIMARY_600};
        > svg {
          visibility: inherit;
        }
      }
    `};

  ${props => props.disabled === true &&
    css `
      cursor: not-allowed;
      color: ${({ theme }) => theme.colors.INK_400};
      ${Checkbox} {
        border: 1px solid ${({ theme }) => theme.colors.INK_200};
      }
    `};

  ${props => props.disabled === false &&
    css `
      &:hover {
        color: ${({ theme }) => theme.colors.INK_700};
        ${Checkbox} {
          border: 1px solid ${({ theme }) => theme.colors.PRIMARY_600};
        }
      }
    `};
`;
export const SelectorWrapper = styled.span `
  ${props => props.themeColor &&
    css `
      color: ${({ theme }) => theme.colors[props.themeColor]};
    `};
`;
class Dropdown extends React.Component {
    constructor(props) {
        super(props);
        this.isOptionSelected = (option) => {
            const { value } = this.props;
            return value.filter(v => v === option.value).length > 0;
        };
        this.closeWrapper = () => {
            const { onClose } = this.props;
            onClose();
            this.setState({ inputValue: '' });
        };
        this.handleOptionChange = (option) => () => {
            const { onChange, type, value } = this.props;
            let currentValue = [];
            if (type === 'multipleOption') {
                if (this.isOptionSelected(option)) {
                    currentValue = value.filter(element => element !== option.value);
                }
                else {
                    currentValue = value.concat(option.value);
                }
            }
            else if (type === 'singleOption') {
                currentValue = currentValue.concat(option.value);
                this.closeWrapper();
            }
            if (onChange) {
                onChange(currentValue);
            }
        };
        this.handleClickOutside = (event) => {
            const { isOpen } = this.props;
            if (this.dropdownContainerRef &&
                this.dropdownContainerRef.current &&
                !this.dropdownContainerRef.current.contains(event.target) &&
                isOpen) {
                this.closeWrapper();
            }
        };
        this.handleValueToLabel = (value) => {
            const { options } = this.props;
            const currentOption = options.find(option => option.value === value);
            if (currentOption) {
                return currentOption.label;
            }
            return '';
        };
        this.filterOption = (option, inputValue = '') => {
            var _a;
            const { filterOption, optionFilterProp } = this.props;
            if (filterOption)
                return filterOption(option, inputValue);
            return (_a = option[optionFilterProp || 'label']) === null || _a === void 0 ? void 0 : _a.includes(inputValue);
        };
        this.handleInputChange = (e) => {
            this.setState({ inputValue: e.target.value });
        };
        this.getFilteredOptions = () => {
            const { options, inputValue: propsInputValue, onInputChange } = this.props;
            const { inputValue: stateInputValue } = this.state;
            const inputValue = onInputChange ? propsInputValue : stateInputValue;
            return options.filter(option => this.filterOption(option, inputValue));
        };
        this.renderSelectorContent = () => {
            const { placeHolderColor, placeHolder, value, keepPlaceHolder, } = this.props;
            return (React.createElement(SelectorWrapper, { ref: this.selectorRef, themeColor: placeHolderColor || 'INK_800' }, value.length === 0 || keepPlaceHolder
                ? placeHolder
                : value.map((v, index) => (React.createElement("span", { key: v }, index === 0
                    ? `${this.handleValueToLabel(v)}`
                    : `,${this.handleValueToLabel(v)}`)))));
        };
        this.renderDefaultSelector = () => {
            const { isOpen, placeHolder, width, minHeight, disabledShadow, contentPadding, border, useDefaultBorder, onClick, enableSearch, inputValue: propsInputValue, onInputChange, } = this.props;
            const { inputValue: stateInputValue } = this.state;
            const inputValue = onInputChange ? propsInputValue : stateInputValue;
            return (React.createElement(Selector, { isOpen: isOpen, onClick: onClick, width: width, border: border, minHeight: minHeight, disabledShadow: disabledShadow, useDefaultBorder: useDefaultBorder, contentPadding: contentPadding },
                enableSearch && isOpen ? (React.createElement(StyledInput, { ref: this.inputRef, placeholder: placeHolder, value: inputValue, onClick: e => e.stopPropagation(), onChange: onInputChange || this.handleInputChange })) : (this.renderSelectorContent()),
                React.createElement(Chevron, { position: isOpen ? 'TOP' : 'BOTTOM', size: "MEDIUM" })));
        };
        this.dropdownContainerRef = React.createRef();
        this.inputRef = React.createRef();
        this.selectorRef = React.createRef();
        this.state = {
            inputValue: '',
        };
    }
    componentDidMount() {
        if (typeof window !== 'undefined') {
            window.addEventListener('mousedown', this.handleClickOutside);
        }
    }
    componentDidUpdate(prevProps) {
        const { isOpen } = this.props;
        if (!prevProps.isOpen && isOpen && this.inputRef && this.inputRef.current)
            this.inputRef.current.focus();
    }
    componentWillUnmount() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('mousedown', this.handleClickOutside);
        }
    }
    render() {
        const { isOpen, width, options, type, menuAlign, optionMaxHight, optionMarginTop, onClick, renderSelector, className, enableSearch, optionPlaceholder, block, e2eId, } = this.props;
        const displayOptions = enableSearch
            ? this.getFilteredOptions()
            : options;
        return (React.createElement(Container, { block: block, "data-e2e-id": e2eId, className: className, ref: this.dropdownContainerRef },
            renderSelector
                ? renderSelector({
                    onSelectorClick: onClick,
                    isOpen,
                    renderDefaultSelector: this.renderDefaultSelector,
                })
                : this.renderDefaultSelector(),
            React.createElement(DropdownOptionWrapper, { isOpen: isOpen, menuAlign: menuAlign, width: width, optionMaxHight: optionMaxHight, optionMarginTop: optionMarginTop },
                displayOptions.map(option => {
                    const optionDisabled = option.disabled || false;
                    if (type === 'multipleOption') {
                        return (React.createElement(CheckboxWrapper, { selected: this.isOptionSelected(option), disabled: optionDisabled, onClick: optionDisabled ? undefined : this.handleOptionChange(option), key: option.value },
                            React.createElement(Checkbox, null,
                                React.createElement(Tick, { size: "SMALL", color: "PRIMARY_600" })),
                            React.createElement("span", null, option.label)));
                    }
                    return (React.createElement(DropdownOption, { e2eId: option.e2eId, key: option.key, selected: this.isOptionSelected(option), disabled: optionDisabled, onClick: this.handleOptionChange(option), color: option.color || 'INK_800', iconColor: option.iconColor || option.color || 'INK_800', Icon: option.Icon, iconDirection: option.iconDirection, label: option.label, customLabel: option.customLabel }));
                }),
                enableSearch && optionPlaceholder && !displayOptions.length && (React.createElement(DropdownOption, { e2eId: optionPlaceholder.e2eId, color: optionPlaceholder.color || 'INK_400', iconColor: optionPlaceholder.iconColor ||
                        optionPlaceholder.color ||
                        'INK_800', selected: false, Icon: optionPlaceholder.Icon, label: optionPlaceholder.label, disabled: true })))));
    }
}
Dropdown.defaultProps = {
    type: 'singleOption',
    menuAlign: 'left',
    width: '160px',
    optionFilterProp: 'label',
};
export default Dropdown;
//# sourceMappingURL=Dropdown.js.map